import { ref, onUnmounted } from 'vue';

/**
 * Composable for handling Server-Sent Events (SSE)
 * 
 * @param {string} url - The SSE endpoint URL
 * @param {Object} eventHandlers - Object mapping event types to handler functions
 * @returns {Object} - Connection control methods and state
 * 
 * @example
 * const { connect, disconnect, isConnected, error } = useServerSentEvents(
 *   '/api/ai-feedback/stream/123',
 *   {
 *     progress: (data) => console.log('Progress:', data),
 *     complete: (data) => console.log('Complete:', data),
 *     error: (data) => console.error('Error:', data)
 *   }
 * );
 * 
 * connect(); // Start listening to SSE
 */
export function useServerSentEvents(url, eventHandlers = {}) {
  const isConnected = ref(false);
  const error = ref(null);
  let eventSource = null;
  let reconnectTimeout = null;
  let shouldReconnect = true;

  /**
   * Connect to the SSE endpoint
   */
  const connect = () => {
    if (eventSource) {
      return; // Already connected
    }

    try {
      error.value = null;
      eventSource = new EventSource(url);

      // Connection opened
      eventSource.addEventListener('connected', (event) => {
        isConnected.value = true;
        error.value = null;
        const data = JSON.parse(event.data);
        if (eventHandlers.connected) {
          eventHandlers.connected(data);
        }
      });

      // Register custom event handlers
      Object.keys(eventHandlers).forEach((eventType) => {
        if (eventType !== 'connected' && eventType !== 'error' && eventType !== 'open' && eventType !== 'close') {
          eventSource.addEventListener(eventType, (event) => {
            try {
              const data = JSON.parse(event.data);
              eventHandlers[eventType](data);
            } catch (err) {
              console.error(`Error parsing ${eventType} event data:`, err);
            }
          });
        }
      });

      // Handle errors
      eventSource.onerror = (event) => {
        console.error('SSE error:', event);
        isConnected.value = false;
        
        // Don't reconnect if we explicitly disconnected
        if (shouldReconnect && eventSource.readyState === EventSource.CLOSED) {
          error.value = 'Connection lost. Attempting to reconnect...';
          
          // Try to reconnect after 3 seconds
          reconnectTimeout = setTimeout(() => {
            if (shouldReconnect) {
              console.log('Attempting to reconnect to SSE...');
              disconnect(false); // Clean up without preventing reconnection
              connect();
            }
          }, 3000);
        }

        if (eventHandlers.error) {
          eventHandlers.error({ message: 'Connection error' });
        }
      };

      // Connection opened (native event)
      eventSource.onopen = () => {
        console.log('SSE connection opened');
        error.value = null;
        if (eventHandlers.open) {
          eventHandlers.open();
        }
      };

    } catch (err) {
      console.error('Failed to create EventSource:', err);
      error.value = err.message;
      isConnected.value = false;
    }
  };

  /**
   * Disconnect from the SSE endpoint
   * @param {boolean} preventReconnect - If true, prevents automatic reconnection
   */
  const disconnect = (preventReconnect = true) => {
    if (preventReconnect) {
      shouldReconnect = false;
    }

    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }

    if (eventSource) {
      eventSource.close();
      eventSource = null;
      isConnected.value = false;
    }
  };

  // Cleanup on component unmount
  onUnmounted(() => {
    disconnect(true);
  });

  return {
    connect,
    disconnect,
    isConnected,
    error
  };
}
